<template>
  <section class="dash-panel">
    <div class="dash-panel__header">
      <div>
        <span class="dash-panel__eyebrow">第二层</span>
        <h2>家长家庭{{ selectedTeacherName ? ' · ' + selectedTeacherName : '' }}</h2>
      </div>
      <div class="dash-meta">
        <span>{{ parents.length }} 个家庭</span>
      </div>
    </div>
    <div :class="['family-list', { 'family-list--fill': fillHeight }]">
      <button
        v-for="parent in parents"
        :key="parent.customerId"
        type="button"
        :class="['family-card', { 'is-active': parent.customerId === activeParentId }]"
        @click="onParentClick(parent)"
      >
        <div class="family-card__header">
          <div class="family-card__info">
            <strong class="family-card__name">{{ parent.parentName }}</strong>
            <span v-if="parent.childName" class="family-card__child">{{ parent.childName }}<template v-if="parent.childGrade"> · {{ parent.childGrade }}</template></span>
          </div>
          <span class="family-card__status" :class="statusClass(parent.status)">{{ parent.status || '待跟进' }}</span>
        </div>
        <div class="family-card__details">
          <span v-if="parent.parentPhone" class="family-card__phone">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
            {{ parent.parentPhone }}
          </span>
           <span v-if="parent.addTime" class="family-card__add-time">添加于 {{ formatDate(parent.addTime) }}</span>
          <span v-if="parent.lastCommTime" class="family-card__last-comm">最近沟通 {{ formatLastComm(parent.lastCommTime) }}</span>
        </div>
      </button>
    </div>
  </section>
</template>

<script setup>
import { ref, watch } from 'vue'
import { getParents } from '@/api'

const props = defineProps({
  campPeriodName: { type: String, default: '' },
  selectedTeacherName: { type: String, default: '' },
  autoSelectFirst: { type: Boolean, default: false },
  fillHeight: { type: Boolean, default: false },
})

const parents = ref([])
const activeParentId = ref('')

const emit = defineEmits(['selectParent'])

function normalizeParent(parent) {
  return {
    ...parent,
    customerId: parent.customerId,
    saleId: parent.saleId,
    parentName: parent.parentName,
  }
}

function selectParent(parent, options = {}) {
  activeParentId.value = parent.customerId
  emit('selectParent', normalizeParent(parent), options)
}

function onParentClick(parent) {
  selectParent(parent, { auto: false })
}

function parentIssueScore(parent) {
  if (parent.qcStatus === 'suggested_violation' || parent.status === '违规') return 3
  if (parent.qcStatus === 'needs_review' || parent.status === '待复核') return 2
  if (parent.status === '异常' || parent.status === '风险') return 1
  return 0
}

function pickDefaultParent(list) {
  if (!list.length) return null
  return list.reduce((best, parent) => {
    if (!best) return parent
    return parentIssueScore(parent) > parentIssueScore(best) ? parent : best
  }, null)
}

async function fetchParents() {
  if (!props.campPeriodName || !props.selectedTeacherName) return
  try {
    const res = await getParents({
      campPeriodName: props.campPeriodName,
      teacherName: props.selectedTeacherName,
    })
    if (Array.isArray(res)) {
      parents.value = res
    } else if (res.code === 200 && res.data) {
      parents.value = res.data
    }
    if (props.autoSelectFirst) {
      const selectedStillExists = parents.value.some((parent) => parent.customerId === activeParentId.value)
      if (!selectedStillExists) {
        activeParentId.value = ''
      }
      if (parents.value.length > 0 && !activeParentId.value) {
        selectParent(pickDefaultParent(parents.value), { auto: true })
      }
    }
  } catch (err) {
    console.error('获取家长列表失败', err)
  }
}

watch(() => [props.campPeriodName, props.selectedTeacherName], fetchParents, { immediate: true })

function statusClass(status) {
  if (status === '正常') return 'family-card__status--ok'
  if (status === '违规' || status === '异常' || status === '风险') return 'family-card__status--warn'
  if (status === '待复核') return 'family-card__status--review'
  return ''
}

function formatDate(dateStr) {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return dateStr.slice(0, 10)
  return `${d.getMonth() + 1}月${d.getDate()}日`
}

function formatLastComm(dateStr) {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return dateStr.slice(0, 10)
  const now = new Date()
  const diff = now - d
  if (diff < 86400000) return '今天'
  if (diff < 172800000) return '昨天'
  if (diff < 604800000) return `${Math.floor(diff / 86400000)}天前`
  return `${d.getMonth() + 1}月${d.getDate()}日`
}
</script>

<style scoped>
.family-list {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  /* max-height: 520px; */
  overflow-y: auto;
  overscroll-behavior-y: auto;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
  padding-right: 2px;
}

.family-list::-webkit-scrollbar {
  display: none;
}

.family-list--fill {
  flex: 1;
  min-height: 0;
  max-height: none;
}

.family-card {
  display: block;
  width: 100%;
  padding: 12px 14px;
  border: 1px solid rgba(29, 47, 44, 0.06);
  border-radius: 12px;
  background: rgba(255, 250, 244, 0.4);
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: background 140ms ease, border-color 140ms ease;
}

.family-card:hover {
  background: rgba(41, 107, 115, 0.04);
  border-color: rgba(41, 107, 115, 0.12);
}

.family-card.is-active {
  background: rgba(41, 107, 115, 0.08);
  border-color: rgba(41, 107, 115, 0.2);
}

.family-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.family-card__info {
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
}

.family-card__name {
  font-size: 0.84rem;
  font-weight: 600;
  color: var(--ink);
  white-space: nowrap;
}

.family-card__child {
  font-size: 0.68rem;
  color: var(--ink-soft);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.family-card__status {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 0.6rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  background: rgba(29, 47, 44, 0.06);
  color: var(--ink-soft);
  flex-shrink: 0;
}

.family-card__status--ok {
  background: rgba(43, 139, 100, 0.1);
  color: #2b8b64;
}

.family-card__status--warn {
  background: rgba(203, 97, 69, 0.1);
  color: #cb6145;
}

.family-card__status--review {
  background: rgba(210, 140, 40, 0.12);
  color: #a9650f;
}

.family-card__details {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 8px;
}

.family-card__phone,
.family-card__sale {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.68rem;
  color: var(--ink-soft);
}

.family-card__phone svg,
.family-card__sale svg {
  flex-shrink: 0;
  opacity: 0.5;
}

.family-card__add-time,
.family-card__last-comm {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.64rem;
  color: rgba(29, 47, 44, 0.42);
  white-space: nowrap;
}

.family-card__add-time::before {
  content: '';
  display: inline-block;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: rgba(29, 47, 44, 0.2);
}

.family-card__last-comm {
  color: rgba(41, 107, 115, 0.6);
}

.family-card__last-comm::before {
  content: '';
  display: inline-block;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: rgba(41, 107, 115, 0.35);
}

@media (max-width: 860px) {
  .family-card__header {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .family-card__details {
    flex-wrap: wrap;
    gap: 8px;
  }

  .family-list {
    max-height: none;
  }
}
</style>
